import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Region, Shapeable } from '@shapeable/types';
import { AspectRatio, ColorTokens, breakpoints, theme } from '@shapeable/theme';
import { CellHeader, ContentTitle, IconTextValue, VideoPlayerSitu, useEntity, useLang, useMonthNames } from '@shapeable/ui';
import { classNames, formatDateString } from '@shapeable/utils';
import { UnitIcon } from '@shapeable/icons';
import { Video } from '@shapeable/kelp-forest-alliance-types';

const cls = classNames('video-details-header');

// -------- Types -------->

export type VideoDetailsHeaderProps = Classable & HasChildren & {
  entity?: Video;
  videoAspectRatio?: AspectRatio;
  color?: string;
  badgeColor?: string;
  colors?: ColorTokens.TextBackgroundBorderStrokeFill;
};

export const VideoDetailsHeaderDefaultProps: Omit<VideoDetailsHeaderProps, 'entity'> = {
  color: '#FFFFFF',
  videoAspectRatio: {
    base: 16 / 9,
    tablet: 16 / 9,
    desktop: 16 / 10,
  },
  colors: {
    text: 'strong',
    stroke: 'strong',
    border: 'strong',
    fill: 'strong',
    hover: {
      text: 'button-hover',
      border: 'button-hover',
      stroke: 'button-hover',
      fill: 'button-hover',
    },
  },
};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    margin: ${theme.UNIT(10)} 0;
    ${theme.APPLY_COLORS};
  `,
});

const LayoutStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    gap: ${theme.UNIT(8)};
    align-items: center;
  `,
  desktop: css`
    flex-direction: row;
  `,
});

const VideoStyles = breakpoints({
  base: css`
    width: 100%;
    box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.3);
  `,
});

const BodyStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(3)};
    flex-shrink: 0;
    width: 100%;
  `,
  desktop: css`
    width: 40%;
  `,
});

const TitlesStyles = breakpoints({
  base: css`
    font-size: 1.3em;
    line-height: 1.1;
    margin: 0;
  `,
});

const PreTitleStyles = breakpoints({
  base: css`
    padding-bottom: ${theme.UNIT(2)};
    .shp--badge {
      padding: ${theme.UNIT(1.5)} ${theme.UNIT(2)};
    }
    color: ${theme.COLOR('strong')};
  `,
});

const TitleStyles = breakpoints({
  base: css`
    h1 {
      font-size: 1.5em;
      margin: 0;
    }
  `,
});

const InfoStyles = breakpoints({
  base: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.UNIT(5)};
    padding-top: ${theme.UNIT(5)};
    ${theme.H_DOT_BG()};
  `,
});

const PublishedStyles = breakpoints({
  base: css`
    font-weight: 500;
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div`${ContainerStyles}`,
  Layout: styled.div.attrs(cls.attr('layout'))`${LayoutStyles}`,
  Video: styled(VideoPlayerSitu)`${VideoStyles}`,
  Body: styled.div`${BodyStyles}`,
  Titles: styled.div.attrs(cls.attr('titles'))`${TitlesStyles}`,
  PreTitle: styled(CellHeader)`${PreTitleStyles}`,
  Title: styled(ContentTitle)`${TitleStyles}`,
  Info: styled.div.attrs(cls.attr('info'))`${InfoStyles}`,
  Published: styled(IconTextValue)`${PublishedStyles}`,
};

export const VideoDetailsHeader: Shapeable.FC<VideoDetailsHeaderProps> = (props) => {
  const { className, children, videoAspectRatio, color, badgeColor, colors } = props;
  const entity = useEntity(props.entity);
  const { name, published, url, _schema = {}, types = [] } = entity;
  const hasTypes = !!types.length;
  const videoType = types[0]?.name || _schema.label;

  const t = useLang();
  const monthNames = useMonthNames();
  const iconTextValueColors = { color: colors.text, fill: colors.text };

  return (
    <My.Container className={cls.name(className)}>
      <My.Layout>
        {url && <My.Video url={url} aspectRatio={videoAspectRatio} />}
        <My.Body>
          <My.Titles>
            <My.PreTitle badge={hasTypes && videoType} colors={{ background: badgeColor }} >
              {t('Video')}
            </My.PreTitle>
            <My.Title colors={colors}>{name}</My.Title>
          </My.Titles>
          {published && (
            <My.Info>
              <My.Published
                label="Published"
                colors={iconTextValueColors}
                iconComponent={UnitIcon}
                iconSize={40}
              >
                {formatDateString(published, t('Do MMM YYYY'), 'YYYY-MM-DD', { monthNames })}
              </My.Published>
            </My.Info>
          )}
        </My.Body>
      </My.Layout>
      {children}
    </My.Container>
  );
};

VideoDetailsHeader.defaultProps = VideoDetailsHeaderDefaultProps;
VideoDetailsHeader.cls = cls;
